import * as Yup from "yup";
import { validationResult } from "./patterns";
import { runFlatFormSchema } from "./yupFormUtils";

const DDOS_SENSITIVITY_MIN = 1;
const DDOS_SENSITIVITY_MAX = 3;
const DDOS_TIMEOUT_VALUES = new Set([30, 60, 600, 3600, 86400]);

function validateDdosAdConfigField(key, value, type) {
  const trimmed = String(value ?? "").trim();
  if (!trimmed) return validationResult(true);

  if (type === "float") {
    const n = parseFloat(trimmed);
    if (!Number.isFinite(n)) return validationResult(false, "Enter a valid number");
    if (n < 0) return validationResult(false, "Must be zero or greater");
    return validationResult(true);
  }

  if (!/^-?\d+$/.test(trimmed)) {
    return validationResult(false, "Enter a whole number");
  }
  const n = parseInt(trimmed, 10);
  if (!Number.isFinite(n)) return validationResult(false, "Enter a valid number");

  if (key === "sensitivity" && (n < DDOS_SENSITIVITY_MIN || n > DDOS_SENSITIVITY_MAX)) {
    return validationResult(false, `Sensitivity must be between ${DDOS_SENSITIVITY_MIN} and ${DDOS_SENSITIVITY_MAX}`);
  }
  if (key === "timeout" && n < 1) {
    return validationResult(false, "Timeout must be at least 1 second");
  }
  if (n < 0) return validationResult(false, "Must be zero or greater");
  return validationResult(true);
}

export function validateDdosSettingsForm({
  sensitivity,
  timeout,
  adFormValues,
  adConfigGroups,
} = {}) {
  const adFieldShape = {};
  if (adFormValues && adConfigGroups) {
    adConfigGroups.forEach((group) => {
      group.fields.forEach((f) => {
        adFieldShape[f.key] = Yup.string().test(f.key, function (value) {
          const result = validateDdosAdConfigField(f.key, value, f.type);
          return result.valid || this.createError({ message: result.message });
        });
      });
    });
  }

  const schema = Yup.object({
    sensitivity: Yup.mixed().test("sensitivity", function (value) {
      const sens = typeof value === "number" ? value : parseInt(value, 10);
      if (!Number.isFinite(sens) || sens < DDOS_SENSITIVITY_MIN || sens > DDOS_SENSITIVITY_MAX) {
        return this.createError({ message: "Select a valid sensitivity level" });
      }
      return true;
    }),
    timeout: Yup.mixed().test("timeout", function (value) {
      const timeoutVal = typeof value === "number" ? value : parseInt(value, 10);
      if (!Number.isFinite(timeoutVal) || !DDOS_TIMEOUT_VALUES.has(timeoutVal)) {
        return this.createError({ message: "Select a valid mitigation timeout" });
      }
      return true;
    }),
    ...adFieldShape,
  });

  const values = {
    sensitivity,
    timeout,
    ...(adFormValues || {}),
  };

  return runFlatFormSchema(schema, values);
}
